import React, { useState, useEffect } from 'react';
import { FaTicketAlt, FaLock, FaPaperclip, FaSpinner } from 'react-icons/fa';
import { requestSupportOtpAPI, verifyOtpAndFetchTicketAPI, addPublicTicketCommentAPI, generateSecureUploadUrlAPI, generateSecureReadUrlAPI, refreshPublicTicketAPI } from '../services/serverService';

export default function PublicTicketTracker() {
    const [step, setStep] = useState(1); // 1: Request, 2: OTP, 3: View Ticket
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');

    const [email, setEmail] = useState('');
    const [ticketId, setTicketId] = useState('');
    const [otp, setOtp] = useState('');
    
    const [ticketData, setTicketData] = useState(null);
    const [comments, setComments] = useState([]);
    
    const [replyMessage, setReplyMessage] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [trackAnotherId, setTrackAnotherId] = useState('');
    const [trackAnotherError, setTrackAnotherError] = useState('');
    const [isTrackAnotherLoading, setIsTrackAnotherLoading] = useState(false);

    const handleAttachmentClick = async (fileUrl) => {
        if (!fileUrl) return;
        if (fileUrl.includes('firebasestorage.googleapis.com') && fileUrl.includes('alt=media')) {
            window.open(fileUrl, '_blank');
            return;
        }
        try {
            const res = await generateSecureReadUrlAPI(fileUrl);
            window.open(res.data.data.readUrl, '_blank');
        } catch (error) {
            alert("Failed to securely open file. It may have expired.");
        }
    };

    const handleRequestOtp = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            await requestSupportOtpAPI(email);
            setStep(2);
        } catch (err) {
            setError('Failed to send verification code. Please check the email.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const res = await verifyOtpAndFetchTicketAPI(email, otp, ticketId);
            setTicketData(res.data.data.ticket);
            setComments(res.data.data.comments);
            setStep(3);
            
            // Persist session securely in localStorage (survives tab close, expires in 24h)
            localStorage.setItem('support_tracker_session', JSON.stringify({
                email,
                otp,
                ticketId,
                timestamp: Date.now()
            }));
        } catch (err) {
            setError('Invalid OTP or Ticket ID, or the email does not match this ticket.');
            localStorage.removeItem('support_tracker_session');
        } finally {
            setIsLoading(false);
        }
    };

    // Auto-login if session exists and is under 24 hours old
    useEffect(() => {
        const savedSession = localStorage.getItem('support_tracker_session');
        if (savedSession) {
            try {
                const parsed = JSON.parse(savedSession);
                const isExpired = Date.now() - parsed.timestamp > 24 * 60 * 60 * 1000;
                
                if (!isExpired && parsed.email && parsed.otp && parsed.ticketId) {
                    setEmail(parsed.email);
                    setOtp(parsed.otp);
                    setTicketId(parsed.ticketId);
                    
                    // Silent auto-verify using refresh (doesn't need OTP)
                    refreshPublicTicketAPI({ email: parsed.email, ticketId: parsed.ticketId })
                        .then(res => {
                            setTicketData(res.data.data.ticket);
                            setComments(res.data.data.comments);
                            setStep(3);
                        })
                        .catch(() => {
                            localStorage.removeItem('support_tracker_session');
                        });
                } else {
                    localStorage.removeItem('support_tracker_session');
                }
            } catch (e) {
                localStorage.removeItem('support_tracker_session');
            }
        }
    }, []);

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setIsUploading(true);
        setError('');

        try {
            const newAttachments = [];
            for (const file of files) {
                const isVideo = file.type.startsWith('video/');
                const maxSize = isVideo ? 20 * 1024 * 1024 : 5 * 1024 * 1024;
                if (file.size > maxSize) throw new Error(`File ${file.name} is too large.`);

                const res = await generateSecureUploadUrlAPI({ fileName: file.name, fileType: file.type, fileSize: file.size });
                const { uploadUrl, fileUrl, mimeType, fileName } = res.data.data;

                const uploadResponse = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': mimeType }, body: file });
                if (!uploadResponse.ok) throw new Error('Upload failed.');

                newAttachments.push({ fileName, fileUrl, mimeType, uploadedAt: new Date().toISOString() });
            }
            setAttachments(prev => [...prev, ...newAttachments]);
        } catch (err) {
            setError(err.message || 'File upload failed.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleSendReply = async (e) => {
        e.preventDefault();
        if (!replyMessage.trim() && attachments.length === 0) return;

        setIsLoading(true);
        try {
            const res = await addPublicTicketCommentAPI(ticketId, email, replyMessage, attachments);
            setComments(prev => [...prev, { ...res.data.data, createdByUid: 'CUSTOMER', createdByEmail: email }]);
            setReplyMessage('');
            setAttachments([]); // Clear attachments after sending
        } catch (err) {
            setError('Failed to send reply.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleTrackAnother = async () => {
        if (!trackAnotherId.trim()) return;
        setIsTrackAnotherLoading(true);
        setTrackAnotherError('');
        try {
            const trimmedId = trackAnotherId.trim().toUpperCase();
            const res = await refreshPublicTicketAPI({ ticketId: trimmedId, email });
            setTicketId(trimmedId);
            setTicketData(res.data.data.ticket);
            setComments(res.data.data.comments || []);
            setTrackAnotherId('');
        } catch (err) {
            setTrackAnotherError('Ticket not found or you are not authorized to view it.');
        } finally {
            setIsTrackAnotherLoading(false);
        }
    };

    if (step === 3 && ticketData) {
        return (
            <div style={{ height: '100vh', overflowY: 'auto', backgroundColor: '#f4f7fa', padding: '40px 20px', fontFamily: 'sans-serif', boxSizing: 'border-box' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                    
                    {/* Header */}
                    <div style={{ backgroundColor: '#2b6cb0', color: 'white', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '20px' }}>{ticketData.ticketId} - {ticketData.subject}</h2>
                            <p style={{ margin: '5px 0 0 0', opacity: 0.8, fontSize: '14px' }}>Category: {ticketData.category}</p>
                        </div>
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                            <button 
                                onClick={async () => {
                                    const res = await refreshPublicTicketAPI({ ticketId, email });
                                    setTicketData(res.data.data.ticket);
                                    setComments(res.data.data.comments || []);
                                }}
                                style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                            >
                                ↻ Refresh Chat
                            </button>
                            <span style={{ backgroundColor: ticketData.status === 'Resolved' ? '#38a169' : '#ecc94b', color: ticketData.status === 'Resolved' ? 'white' : 'black', padding: '5px 12px', borderRadius: '20px', fontWeight: 'bold', fontSize: '12px' }}>
                                {ticketData.status}
                            </span>
                        </div>
                    </div>

                    {/* Track Another Ticket Bar */}
                    <div style={{ backgroundColor: '#edf2f7', padding: '10px 20px', display: 'flex', flexDirection: 'column', gap: '5px', borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#4a5568' }}>Track Another Ticket:</span>
                            <input 
                                type="text" 
                                placeholder="Enter Ticket ID (e.g. TKT-1234)" 
                                value={trackAnotherId}
                                onChange={(e) => setTrackAnotherId(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleTrackAnother()}
                                style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                            />
                            <button 
                                onClick={handleTrackAnother} 
                                disabled={isTrackAnotherLoading || !trackAnotherId.trim()}
                                style={{ padding: '8px 16px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', opacity: (isTrackAnotherLoading || !trackAnotherId.trim()) ? 0.7 : 1 }}
                            >
                                {isTrackAnotherLoading ? 'Searching...' : 'Track'}
                            </button>
                        </div>
                        {trackAnotherError && <div style={{ color: '#e53e3e', fontSize: '12px', fontWeight: 'bold' }}>{trackAnotherError}</div>}
                    </div>


                    <div style={{ padding: '20px' }}>
                        {/* Original Description */}
                        <div style={{ marginBottom: '30px' }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#4a5568' }}>Original Request</h4>
                            <div style={{ backgroundColor: '#f7fafc', padding: '15px', borderRadius: '6px', border: '1px solid #e2e8f0', whiteSpace: 'pre-wrap', color: '#2d3748' }}>
                                {ticketData.description}
                                
                                {ticketData.attachments && ticketData.attachments.length > 0 && (
                                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #cbd5e0' }}>
                                        <strong>Attachments:</strong>
                                        <ul style={{ paddingLeft: '20px', margin: '5px 0 0 0', fontSize: '14px' }}>
                                            {ticketData.attachments.map((att, i) => (
                                                <li key={i}>
                                                    <span 
                                                        onClick={() => handleAttachmentClick(att.fileUrl || att.url)} 
                                                        style={{ color: '#3182ce', cursor: 'pointer', textDecoration: 'underline' }}
                                                    >
                                                        {att.fileName || att.name || 'Unnamed File'}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Comments Thread */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '30px', maxHeight: '400px', overflowY: 'auto', paddingRight: '10px' }}>
                            {comments.map((comment, idx) => {
                                const isCustomer = comment.createdByUid === 'CUSTOMER';
                                return (
                                    <div key={idx} style={{ alignSelf: isCustomer ? 'flex-end' : 'flex-start', maxWidth: '80%', backgroundColor: isCustomer ? '#ebf8ff' : '#f0fff4', padding: '15px', borderRadius: '8px', border: `1px solid ${isCustomer ? '#bee3f8' : '#c6f6d5'}` }}>
                                        <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#718096', marginBottom: '5px' }}>
                                            {isCustomer ? 'You' : 'Support Team'} • {comment.createdAt ? (comment.createdAt.toDate ? comment.createdAt.toDate().toLocaleString() : new Date(comment.createdAt).toLocaleString()) : ''}
                                        </div>
                                        <div style={{ color: '#2d3748', whiteSpace: 'pre-wrap' }}>{comment.message}</div>
                                        
                                        {comment.attachments && comment.attachments.length > 0 && (
                                            <div style={{ marginTop: '10px', fontSize: '13px' }}>
                                                {comment.attachments.map((att, i) => (
                                                    <div key={i}>
                                                        📎 <span 
                                                            onClick={() => handleAttachmentClick(att.fileUrl || att.url)} 
                                                            style={{ color: '#3182ce', cursor: 'pointer', textDecoration: 'underline' }}
                                                        >
                                                            {att.fileName || att.name || 'Unnamed File'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Reply Box */}
                        {ticketData.status !== 'Archived' && (
                            <form onSubmit={handleSendReply} style={{ backgroundColor: '#f7fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                {error && <div style={{ color: 'red', marginBottom: '10px', fontSize: '14px' }}>{error}</div>}
                                <textarea 
                                    value={replyMessage}
                                    onChange={e => setReplyMessage(e.target.value)}
                                    placeholder="Type your reply here..."
                                    style={{ width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '4px', border: '1px solid #cbd5e0', minHeight: '100px', marginBottom: '10px' }}
                                />
                                
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', color: '#4a5568', fontSize: '14px', fontWeight: 'bold' }}>
                                        <FaPaperclip /> Attach Files
                                        <input type="file" multiple accept="image/*,application/pdf,video/*" onChange={handleFileUpload} style={{ display: 'none' }} disabled={isUploading} />
                                    </label>
                                    
                                    <button type="submit" disabled={isLoading || isUploading || (!replyMessage.trim() && attachments.length === 0)} style={{ padding: '10px 24px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', opacity: (isLoading || isUploading) ? 0.7 : 1 }}>
                                        {isLoading ? 'Sending...' : 'Send Reply'}
                                    </button>
                                </div>

                                {isUploading && <div style={{ marginTop: '10px', color: '#3182ce', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}><FaSpinner className="fa-spin" /> Uploading securely...</div>}
                                {attachments.length > 0 && (
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                                        {attachments.map((f, i) => (
                                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: '#e2e8f0', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                                                📎 <span style={{ color: '#4a5568' }}>{f.fileName}</span>
                                                <span onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} style={{ cursor: 'pointer', color: '#e53e3e', fontWeight: 'bold', marginLeft: '5px', fontSize: '14px' }}>&times;</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </form>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ height: '100vh', overflowY: 'auto', backgroundColor: '#f4f7fa', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', fontFamily: 'sans-serif', boxSizing: 'border-box' }}>
            <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '400px', width: '100%' }}>
                <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <FaTicketAlt style={{ fontSize: '32px', color: '#3182ce', marginBottom: '10px' }} />
                    <h2 style={{ margin: 0, color: '#2d3748' }}>Track Support Ticket</h2>
                    <p style={{ margin: 0, color: '#718096', fontSize: '14px' }}>Enter your details to view status securely.</p>
                </div>

                {error && <div style={{ backgroundColor: '#fff5f5', color: '#c53030', padding: '10px', borderRadius: '4px', marginBottom: '20px', fontSize: '14px' }}>{error}</div>}

                {step === 1 && (
                    <form onSubmit={handleRequestOtp} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>TICKET ID</label>
                            <input required type="text" placeholder="e.g. TKT-1005" value={ticketId} onChange={e => setTicketId(e.target.value.toUpperCase())} style={{ width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#4a5568', marginBottom: '5px' }}>WORK EMAIL</label>
                            <input required type="email" placeholder="Email used to create ticket" value={email} onChange={e => setEmail(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                        </div>
                        <button type="submit" disabled={isLoading} style={{ width: '100%', padding: '12px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: isLoading ? 'not-allowed' : 'pointer' }}>
                            {isLoading ? 'Processing...' : 'Send Verification Code'}
                        </button>
                    </form>
                )}

                {step === 2 && (
                    <form onSubmit={handleVerifyOtp} style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
                        <FaLock style={{ fontSize: '32px', color: '#ecc94b' }} />
                        <div style={{ textAlign: 'center' }}>
                            <p style={{ margin: 0, color: '#4a5568', fontSize: '14px' }}>We sent a code to <strong>{email}</strong>.</p>
                        </div>
                        <input required type="text" maxLength="6" placeholder="000000" value={otp} onChange={e => setOtp(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())} style={{ width: '150px', textAlign: 'center', fontSize: '24px', padding: '12px', letterSpacing: '4px', borderRadius: '6px', border: '2px solid #3182ce', fontWeight: 'bold', color: '#2b6cb0' }} />
                        
                        <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                            <button type="button" onClick={() => setStep(1)} style={{ flex: 1, padding: '12px', backgroundColor: '#edf2f7', color: '#4a5568', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Back</button>
                            <button type="submit" disabled={isLoading || otp.length !== 6} style={{ flex: 2, padding: '12px', backgroundColor: '#38a169', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Verify</button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}